import {
    formatPhoneNumber,
    IsoTimestamp,
    PhoneNumberE164,
    SMS_LENGTH_WARNING_THRESHOLD,
    SMS_MAX_LENGTH,
    SmsConsentStatus,
    TelecomPhoneLineId,
    TelecomPhoneLineStatus,
} from "@sp-crm/core";
import { useAgendaRefresh } from "components/marketing-plan/use-agenda-refresh";
import { useDefaultActivityTypeId } from "components/tasks/activity-type-options";
import { SmsConversationHistory } from "components/telecom/sms-conversation-history";
import { fancyPrompt, FancyPromptBehavior } from "components/ui/fancy-prompt";
import { InlineBanner } from "components/ui/inline-banner";
import { defaultLinkStyle } from "components/ui/link";
import { PrimaryButton } from "components/ui/primary-button";
import { SecondaryButton } from "components/ui/secondary-button";
import { Spinner } from "components/ui/spinner";
import { TextArea } from "components/ui/textarea";
import {
    GetPersonalPhoneNumbersQuery,
    GetSmsConversationQuery,
    useGetSmsConversationByPhoneLineQuery,
    useGetSmsConversationQuery,
    useGetTelecomContactStatusQuery,
    useMarkPriorSmsConsentMutation,
    useSendSmsMutation,
} from "generated/graphql";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useProductName } from "store/selectors/branding";
import { usePhoneLines } from "store/selectors/telecom";
import { handleEvent } from "util/user-events";

interface SmsMessageThreadInlineProps {
    phoneLineId: TelecomPhoneLineId;
    phoneNumber: PhoneNumberE164;
    senderName: string;
    isOwnThread: boolean;
}

export const SmsMessageThreadInline: React.FC<SmsMessageThreadInlineProps> = props => {
    const { phoneLineId, phoneNumber, senderName, isOwnThread } = props;
    if (isOwnThread) {
        return <OwnSmsReadPane recipient={phoneNumber} />;
    }
    return (
        <OtherSmsReadPane
            phoneLineId={phoneLineId}
            phoneNumber={phoneNumber}
            senderName={senderName}
        />
    );
};

interface OtherSmsReadPaneProps {
    phoneLineId: TelecomPhoneLineId;
    phoneNumber: PhoneNumberE164;
    senderName: string;
}

const OtherSmsReadPane: React.FC<OtherSmsReadPaneProps> = props => {
    const { phoneLineId, phoneNumber, senderName } = props;
    const scrollRef = useRef<HTMLDivElement>(null);

    const getSmsConversation = useGetSmsConversationByPhoneLineQuery(
        { request: { phoneLineId, phoneNumber } },
        { refetchInterval: 30_000 },
    );

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [getSmsConversation.data]);

    return (
        <div className="flex flex-col h-full">
            <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto px-4 pt-4">
                <SmsConversationHistory
                    messages={
                        getSmsConversation.data?.getSmsConversationByPhoneLine ?? []
                    }
                    showSenderName={true}
                />
            </div>
            <div className="flex-none border-t border-gray-100 px-4 py-4">
                <InlineBanner type="info">
                    This is {senderName}&apos;s conversation with{" "}
                    {formatPhoneNumber(phoneNumber)}.
                </InlineBanner>
            </div>
        </div>
    );
};

interface OwnSmsReadPaneProps {
    recipient: PhoneNumberE164;
}

const OwnSmsReadPane: React.FC<OwnSmsReadPaneProps> = props => {
    const { recipient } = props;
    const [message, setMessage] = useState("");
    const [sendError, setSendError] = useState<string | null>(null);
    const phoneLines = usePhoneLines();
    const sendSms = useSendSmsMutation();
    const refreshActivity = useAgendaRefresh();
    const defaultSmsTypeId = useDefaultActivityTypeId("sms", null);
    const scrollRef = useRef<HTMLDivElement>(null);

    const phoneLineId = useMemo(() => {
        if (phoneLines?.length > 0) {
            return phoneLines[0].id;
        }
        return null;
    }, [phoneLines]);

    const getTelecomContactStatus = useGetTelecomContactStatusQuery(
        { request: { phoneLineId, phoneNumber: recipient } },
        { enabled: !!phoneLineId, refetchInterval: 30_000 },
    );

    const getSmsConversation = useGetSmsConversationQuery(
        { request: { phoneLineId, phoneNumber: recipient } },
        { enabled: !!phoneLineId, refetchInterval: 30_000 },
    );

    const markPriorSmsConsent = useMarkPriorSmsConsentMutation();

    useEffect(() => {
        handleEvent("sms-thread-view", { phoneLineId });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [getSmsConversation.data]);

    const handleSend = useCallback(
        async (e: React.FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            setSendError(null);
            try {
                await sendSms.mutateAsync({
                    request: {
                        from: phoneLines[0].id,
                        to: recipient,
                        body: message,
                        activityTypeId: defaultSmsTypeId,
                    },
                });
                handleEvent("sms-send", {
                    phoneLineId: phoneLines[0].id,
                    messageLength: message.length,
                });
                setMessage("");
                getSmsConversation.refetch();
                refreshActivity();
            } catch (error) {
                setSendError(
                    error instanceof Error
                        ? error.message
                        : "Failed to send message. Please try again.",
                );
            }
        },
        [
            message,
            sendSms,
            phoneLines,
            getSmsConversation,
            refreshActivity,
            defaultSmsTypeId,
            recipient,
        ],
    );

    const handleMessageChange = useCallback(
        (e: React.ChangeEvent<HTMLTextAreaElement>) => {
            setMessage(e.target.value);
        },
        [],
    );

    const handlePriorConsent = useCallback(async () => {
        const date = await fancyPrompt(
            "SMS Consent",
            "Date of consent",
            FancyPromptBehavior.OkCancel,
            "date",
        );
        if (!date) {
            return;
        }
        await markPriorSmsConsent.mutateAsync({
            request: {
                phoneLineId,
                consentedAt: new Date(date).toISOString() as IsoTimestamp,
                phoneNumber: recipient,
            },
        });
        getTelecomContactStatus.refetch();
    }, [markPriorSmsConsent, recipient, phoneLineId, getTelecomContactStatus]);

    if (!phoneLineId) {
        return (
            <div className="p-4">
                <InlineBanner type={"info"}>
                    No phone numbers have been configured to send SMS messages. Navigate
                    to{" "}
                    <Link className={defaultLinkStyle} to={"/settings/account/phone"}>
                        SMS &amp; Voice Settings
                    </Link>{" "}
                    to reserve a phone number.
                </InlineBanner>
            </div>
        );
    }

    const consentStatus =
        getTelecomContactStatus.data?.getTelecomContactStatus.consentStatus;
    const canSend =
        !!getTelecomContactStatus.data?.getTelecomContactStatus.canSend &&
        phoneLines[0].status === TelecomPhoneLineStatus.Active;

    return (
        <div className="flex flex-col h-full">
            <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto px-4 pt-4">
                <SmsConversationHistory
                    messages={getSmsConversation.data?.getSmsConversation ?? []}
                />
            </div>
            <div className="flex-none border-t border-gray-100 px-4 py-4 space-y-4">
                <SendSmsBanner
                    phoneLine={phoneLines[0]}
                    recipient={recipient}
                    consentStatus={consentStatus}
                    onPriorConsent={handlePriorConsent}
                    messages={getSmsConversation.data?.getSmsConversation}
                />
                {sendError ? <InlineBanner type="error">{sendError}</InlineBanner> : null}
                <form onSubmit={handleSend} className="space-y-4">
                    <TextArea
                        required
                        disabled={!canSend}
                        label="Message"
                        maxLength={SMS_MAX_LENGTH}
                        value={message}
                        onChange={handleMessageChange}
                    />
                    {message.length >= SMS_LENGTH_WARNING_THRESHOLD ? (
                        <p
                            className={`text-xs ${
                                message.length >= SMS_MAX_LENGTH
                                    ? "text-red-500"
                                    : "text-gray-500"
                            }`}>
                            {message.length}/{SMS_MAX_LENGTH}
                        </p>
                    ) : null}
                    <div className="flex items-center space-x-2 justify-end">
                        {sendSms.isLoading ? <Spinner /> : null}
                        <PrimaryButton
                            disabled={sendSms.isLoading || !canSend || !message.trim()}
                            type="submit">
                            Send
                        </PrimaryButton>
                    </div>
                </form>
            </div>
        </div>
    );
};

interface SendSmsBannerProps {
    messages: GetSmsConversationQuery["getSmsConversation"];
    phoneLine: GetPersonalPhoneNumbersQuery["getPersonalPhoneNumbers"][number];
    recipient: PhoneNumberE164;
    consentStatus: SmsConsentStatus;
    onPriorConsent: () => void;
}

const SendSmsBanner: React.FC<SendSmsBannerProps> = props => {
    const { messages, phoneLine, recipient, consentStatus, onPriorConsent } = props;
    const productName = useProductName();

    const handlePriorConsent = useCallback(
        async (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            onPriorConsent();
        },
        [onPriorConsent],
    );

    if (phoneLine.status === TelecomPhoneLineStatus.Provisioning) {
        return (
            <InlineBanner type="info">
                Your phone number is still being provisioned, so sending an SMS is not yet
                available. Your phone number should be ready in ten minutes or less.
            </InlineBanner>
        );
    }

    if (
        consentStatus === SmsConsentStatus.NewContact ||
        consentStatus === SmsConsentStatus.None
    ) {
        return (
            <InlineBanner type="warning">
                <div className="space-y-4">
                    <p>
                        This would be your first message to {formatPhoneNumber(recipient)}{" "}
                        from your {productName} phone number. To comply with SMS
                        regulations, you need their consent before sending a message.
                        <br />
                        <br />
                        <ul className="list-disc pl-4">
                            <li>
                                If you obtained consent in writing or through a web form,
                                you can mark it with the button below.
                            </li>
                            <li>
                                If not, have them text &quot;START&quot; to{" "}
                                {formatPhoneNumber(phoneLine.phoneNumber)}.
                            </li>
                        </ul>
                    </p>
                    <SecondaryButton className="text-sm" onClick={handlePriorConsent}>
                        I already have consent
                    </SecondaryButton>
                </div>
            </InlineBanner>
        );
    }

    if (consentStatus === SmsConsentStatus.Unsubscribed) {
        return (
            <InlineBanner type="error">
                <div className="space-y-2">
                    <p>
                        {formatPhoneNumber(recipient)} has unsubscribed from receiving
                        messages from your {productName} phone number. To send an SMS to
                        them, they will need to reply with &quot;START&quot; to
                        re-subscribe.
                    </p>
                </div>
            </InlineBanner>
        );
    }

    if (messages?.length === 0) {
        return (
            <InlineBanner type="info">
                <div className="space-y-2">
                    <p>
                        Since this is your first message to {formatPhoneNumber(recipient)}{" "}
                        from your {productName} phone number, make sure to clearly
                        identify who you are and your purpose for contacting them.
                    </p>
                </div>
            </InlineBanner>
        );
    }

    return null;
};
